import React, { useState, useEffect, useRef } from 'react';
import authService from '../services/authService';
import api from '../services/api';

/* ── Feature item ── */
const FeatureItem = ({ text, delay }) => (
  <div className="feature-item" style={{ animationDelay: delay }}>
    <div className="feature-icon"></div>
    <span className="feature-text">{text}</span>
  </div>
);

/* ── Left branding panel (reused across all screens) ── */
const LeftPanel = ({ subtitle, features }) => (
  <div className="login-left">
    <div className="login-branding">
      <div className="logo-lockup">
        <div className="logo-text">OMKARA</div>
        <div className="logo-divider"></div>
        <div className="logo-tagline">Hostel Management</div>
      </div>
      <p className="login-welcome">{subtitle}</p>
      <div className="login-features">
        {features.map((f, i) => <FeatureItem key={i} text={f} delay={`${0.2 + i * 0.2}s`} />)}
      </div>
    </div>
  </div>
);

/* ── Shared styles ── */
const S = {
  inputWrap:   { position: 'relative' },
  inputPrefix: { position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', fontSize: 15, fontWeight: 600, color: '#6B6B6B', pointerEvents: 'none' },
  errBox:      { background: 'rgba(232,93,74,0.1)', borderLeft: '4px solid #E85D4A', padding: '14px 16px', marginBottom: 24, borderRadius: 2, color: '#E85D4A', fontSize: 14 },
  succBox:     { background: 'rgba(45,95,79,0.08)', borderLeft: '4px solid #2D5F4F', padding: '16px 18px', marginBottom: 24, borderRadius: 2, display: 'flex', alignItems: 'flex-start', gap: 12 },
  succIcon:    { width: 22, height: 22, background: '#2D5F4F', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0, marginTop: 1 },
  succText:    { fontSize: 14, color: '#2D5F4F', fontWeight: 500 },
  backLink:    { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6B6B6B', cursor: 'pointer', textDecoration: 'none', marginBottom: 30, background: 'none', border: 'none', fontFamily: "'Work Sans', sans-serif", padding: 0 },
  divider:     { display: 'flex', alignItems: 'center', gap: 16, margin: '32px 0 28px' },
  dividerLine: { flex: 1, height: 1, background: '#E8E4DF' },
  dividerText: { fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: '#C4BEB8', whiteSpace: 'nowrap' },
  methodToggle:{ display: 'flex', border: '1px solid #E8E4DF', borderRadius: 4, overflow: 'hidden', marginBottom: 28 },
  methodBtn: (active) => ({ flex: 1, padding: '11px', background: active ? '#1B3B5F' : 'transparent', border: 'none', fontSize: 13, fontWeight: 600, fontFamily: "'Work Sans', sans-serif", letterSpacing: '0.5px', color: active ? '#fff' : '#6B6B6B', cursor: 'pointer', transition: 'all 0.25s' }),
  otpGroup:    { display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 4 },
  otpBox: (filled) => ({ flex: 1, height: 56, border: 'none', borderBottom: `2px solid ${filled ? '#D4AF37' : '#E8E4DF'}`, background: 'transparent', fontSize: 24, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif", textAlign: 'center', color: '#1B3B5F', outline: 'none', transition: 'border-color 0.2s', width: '100%' }),
  otpFooter:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 },
  otpTimer:    { fontSize: 13, color: '#6B6B6B' },
  resendBtn: (disabled) => ({ background: 'none', border: 'none', fontSize: 13, fontFamily: "'Work Sans', sans-serif", color: disabled ? '#C4BEB8' : '#D4AF37', fontWeight: 600, cursor: disabled ? 'default' : 'pointer', padding: 0 }),
  formMeta:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -14, marginBottom: 4 },
  forgotLink:  { fontSize: 13, color: '#6B6B6B', cursor: 'pointer', background: 'none', border: 'none', fontFamily: "'Work Sans', sans-serif", padding: 0 },
};

/* ── OTP input row ── */
const OtpRow = ({ values, setValues, inputRefs }) => {
  const handleInput = (val, idx) => {
    const clean = val.replace(/\D/g, '').slice(-1);
    const next = [...values]; next[idx] = clean; setValues(next);
    if (clean && idx < 5 && inputRefs.current[idx + 1]) inputRefs.current[idx + 1].focus();
  };
  const handleBack = (e, idx) => {
    if (e.key === 'Backspace' && !values[idx] && idx > 0 && inputRefs.current[idx - 1]) {
      const next = [...values]; next[idx - 1] = ''; setValues(next);
      inputRefs.current[idx - 1].focus();
    }
  };
  return (
    <div style={S.otpGroup}>
      {values.map((v, i) => (
        <input key={i} ref={el => inputRefs.current[i] = el}
          type="tel" maxLength={1} value={v}
          style={S.otpBox(!!v)}
          onChange={e => handleInput(e.target.value, i)}
          onKeyDown={e => handleBack(e, i)}
        />
      ))}
    </div>
  );
};

/* ── Timer hook ── */
function useTimer() {
  const [timer, setTimer] = useState(0);
  const ref = useRef(null);
  const start = () => {
    if (ref.current) clearInterval(ref.current);
    setTimer(30);
    ref.current = setInterval(() => setTimer(p => { if (p <= 1) { clearInterval(ref.current); return 0; } return p - 1; }), 1000);
  };
  useEffect(() => () => { if (ref.current) clearInterval(ref.current); }, []);
  return [timer, start];
}

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
const LoginScreen = ({ onLogin, onNavigate }) => {

  // ── active screen: 'login' | 'guest' | 'forgot'
  const [screen, setScreen] = useState('login');

  /* ══ LOGIN state ══ */
  const [mobile, setMobile]           = useState('');
  const [loginStep, setLoginStep]     = useState('mobile'); // 'mobile' | 'creds'
  const [loginMethod, setLoginMethod] = useState('password');
  const [password, setPassword]       = useState('');
  const [loginError, setLoginError]   = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginOtp, setLoginOtp]       = useState(['','','','','','']);
  const [loginOtpTimer, startLoginOtpTimer] = useTimer();
  const loginOtpRefs = useRef([]);

  /* ══ FORGOT state ══ */
  const [forgotMobile, setForgotMobile] = useState('');
  const [forgotStep, setForgotStep]     = useState('mobile'); // 'mobile' | 'otp' | 'newpw'
  const [forgotOtp, setForgotOtp]       = useState(['','','','','','']);
  const [forgotTimer, startForgotTimer] = useTimer();
  const forgotOtpRefs = useRef([]);
  const [newPw1, setNewPw1]             = useState('');
  const [newPw2, setNewPw2]             = useState('');
  const [forgotError, setForgotError]   = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  /* ══════════════════
     LOGIN HANDLERS
  ══════════════════ */
  const proceedFromMobile = () => {
    setLoginError('');
    if (mobile.length !== 10) { setLoginError('Please enter a valid 10-digit mobile number.'); return; }
    setLoginStep('creds');
    setLoginMethod('password');
  };

  const switchMethod = (method) => {
    setLoginMethod(method);
    setLoginError('');
    if (method === 'otp') { sendLoginOTP(); }
  };

  const sendLoginOTP = async () => {
    setLoginError('');
    setLoginOtp(['','','','','','']);
    try {
      // ✅ isStaff: true — only registered staff can receive OTP
      // Backend blocks unknown mobiles and auto-creation for staff flow
      await api.post('/auth/send-otp', { mobile, isStaff: true });
      startLoginOtpTimer();
    } catch (err) {
      setLoginError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleLogin = async () => {
    setLoginError('');
    if (loginMethod === 'password') {
      if (!password) { setLoginError('Please enter your password.'); return; }
      setLoginLoading(true);
      try {
        await authService.login(mobile, password);
        onLogin();
      } catch (err) {
        setLoginError(err.message || 'Invalid credentials. Please try again.');
      } finally { setLoginLoading(false); }
    } else {
      const otp = loginOtp.join('');
      if (otp.length < 6) { setLoginError('Please enter the complete 6-digit OTP.'); return; }
      setLoginLoading(true);
      try {
        // ✅ isStaff: true — backend blocks unknown mobiles from logging in
        const res = await api.post('/auth/login-otp', { mobile, otp, isStaff: true });
        if (res.data.token) {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('user', JSON.stringify({
            id:        res.data.id,
            username:  res.data.username,
            email:     res.data.email,
            role:      res.data.role,
            firstName: res.data.firstName || '',
            lastName:  res.data.lastName  || '',
          }));
          onLogin();
        } else {
          setLoginError(res.data.message || 'OTP login failed. Please try again.');
        }
      } catch (err) {
        setLoginError(err.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
      } finally { setLoginLoading(false); }
    }
  };

  const resetLogin = () => {
    setLoginStep('mobile'); setMobile(''); setPassword('');
    setLoginOtp(['','','','','','']); setLoginError('');
  };

  /* ══════════════════
     GUEST HANDLERS
  ══════════════════ */
  const sendGuestOTP = async () => {
    setGuestError('');
    // ✅ Allow Indian (10 digits) or International (+countrycode)
    const isValidIndian = /^[6-9]\d{9}$/.test(guestMobile);
    const isValidIntl   = /^\+[1-9]\d{6,14}$/.test(guestMobile);
    if (!isValidIndian && !isValidIntl) {
      setGuestError('Please enter a valid 10-digit Indian number or international number with country code (e.g. +1XXXXXXXXXX).');
      return;
    }
    setGuestLoading(true);
    try {
      // ✅ No isStaff flag — guest registration flow, allows new mobiles
      await api.post('/auth/send-otp', { mobile: guestMobile });
      setGuestStep('otp');
      setGuestOtp(['','','','','','']);
      startGuestTimer();
    } catch (err) {
      setGuestError(err.response?.data?.message || err.message || 'Failed to send OTP. Please try again.');
    } finally { setGuestLoading(false); }
  };

  const verifyGuestOTP = async () => {
    setGuestError('');
    const otp = guestOtp.join('');
    if (otp.length < 6) { setGuestError('Please enter the complete 6-digit OTP.'); return; }
    setGuestLoading(true);
    try {
      const res = await api.post('/auth/verify-otp', { mobile: guestMobile, otp });
      if (res.data.verified) {
        localStorage.setItem('guestMobile', guestMobile); // ✅ Save BEFORE setTimeout
        setGuestStep('success');
        setTimeout(() => {
          if (onNavigate) onNavigate('registration1');
        }, 1200);
      } else {
        setGuestError('Invalid or expired OTP. Please try again.');
      }
    } catch (err) {
      setGuestError(err.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
    } finally { setGuestLoading(false); }
  };

  const resetGuest = () => {
    setGuestStep('mobile'); setGuestMobile('');
    setGuestOtp(['','','','','','']); setGuestError('');
  };

  /* ══════════════════
     FORGOT HANDLERS
  ══════════════════ */
  const sendForgotOTP = async () => {
    setForgotError('');
    if (forgotMobile.length !== 10) { setForgotError('Please enter a valid 10-digit mobile number.'); return; }
    setForgotLoading(true);
    try {
      await authService.checkMobileExists(forgotMobile);
      // ✅ Forgot password uses staff flow — only registered users
      await api.post('/auth/send-otp', { mobile: forgotMobile, isStaff: true });
      setForgotStep('otp');
      setForgotOtp(['','','','','','']);
      startForgotTimer();
    } catch (err) {
      setForgotError(err.response?.data?.message || err.message || 'Mobile number not registered.');
    } finally { setForgotLoading(false); }
  };

  const verifyForgotOTP = async () => {
    setForgotError('');
    const otp = forgotOtp.join('');
    if (otp.length < 6) { setForgotError('Please enter the complete 6-digit OTP.'); return; }
    setForgotLoading(true);
    try {
      const res = await api.post('/auth/verify-otp', { mobile: forgotMobile, otp });
      if (res.data.verified) {
        setForgotSuccess('OTP verified! You can now set your new password.');
        setForgotStep('newpw');
      } else {
        setForgotError('Invalid or expired OTP. Please try again.');
      }
    } catch (err) {
      setForgotError(err.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
    } finally { setForgotLoading(false); }
  };

  const saveNewPassword = async () => {
    setForgotError('');
    if (!newPw1 || newPw1.length < 6) { setForgotError('Password must be at least 6 characters.'); return; }
    if (newPw1 !== newPw2) { setForgotError('Passwords do not match.'); return; }
    setForgotLoading(true);
    try {
      await authService.resetPasswordByMobile(forgotMobile, newPw1);
      setForgotSuccess('Password updated! Redirecting to login…');
      setTimeout(() => {
        setScreen('login'); resetLogin();
        setForgotStep('mobile'); setForgotMobile('');
        setForgotSuccess(''); setForgotError('');
        setNewPw1(''); setNewPw2('');
      }, 2000);
    } catch (err) {
      setForgotError(err.response?.data?.message || err.message || 'Failed to reset password. Please try again.');
    } finally { setForgotLoading(false); }
  };

  const goToForgot = () => {
    setScreen('forgot');
    setForgotStep('mobile'); setForgotMobile('');
    setForgotOtp(['','','','','','']);
    setForgotError(''); setForgotSuccess('');
    setNewPw1(''); setNewPw2('');
  };

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */
  return (
    <>

      {/* ══════════════════════════════════════
          SCREEN 1 — MAIN LOGIN
      ══════════════════════════════════════ */}
      {screen === 'login' && (
        <div className="login-container">
          <LeftPanel
            subtitle="A unified platform for students, parents, and staff — transparent, real-time, and completely paperless."
            features={[
              'Seamless student registration workflow',
              'Real-time attendance & leave management',
              'Transparent fee tracking & pay card',
              'Instant notifications & alerts',
            ]}
          />

          <div className="login-right">
            <div className="login-card">

              <div className="login-header">
                <h2>Welcome Back</h2>
                <p>Enter your registered mobile number to continue</p>
              </div>

              {loginError && <div style={S.errBox}>⚠️ {loginError}</div>}

              {/* Step A — Mobile */}
              {loginStep === 'mobile' && (
                <div>
                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <div style={S.inputWrap}>
                      <span style={S.inputPrefix}>+91</span>
                      <input type="tel" className="form-input has-prefix"
                        placeholder="98765 43210" maxLength={10}
                        value={mobile}
                        onChange={e => setMobile(e.target.value.replace(/\D/g,''))}
                        onKeyDown={e => e.key === 'Enter' && proceedFromMobile()}
                      />
                    </div>
                  </div>
                  <button className="btn-primary" onClick={proceedFromMobile}>Continue</button>
                </div>
              )}

              {/* Step B — Password / OTP */}
              {loginStep === 'creds' && (
                <div>
                  <p style={{ fontSize: 14, color: '#6B6B6B', marginBottom: 28 }}>
                    Logging in as <strong style={{ color: '#1B3B5F' }}>+91 {mobile.slice(0,5)} {mobile.slice(5)}</strong>
                  </p>

                  <div style={S.methodToggle}>
                    <button style={S.methodBtn(loginMethod === 'password')} onClick={() => switchMethod('password')}>🔒 Password</button>
                    <button style={S.methodBtn(loginMethod === 'otp')}      onClick={() => switchMethod('otp')}>📱 Login with OTP</button>
                  </div>

                  {loginMethod === 'password' && (
                    <div>
                      <div className="form-group" style={{ marginBottom: 10 }}>
                        <label className="form-label">Password</label>
                        <input type="password" className="form-input"
                          placeholder="Enter your password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleLogin()}
                        />
                      </div>
                      <div style={S.formMeta}>
                        <span></span>
                        <button style={S.forgotLink} onClick={goToForgot}>Forgot Password?</button>
                      </div>
                    </div>
                  )}

                  {loginMethod === 'otp' && (
                    <div className="form-group">
                      <label className="form-label">OTP sent to +91 {mobile.slice(0,5)} {mobile.slice(5)}</label>
                      <OtpRow values={loginOtp} setValues={setLoginOtp} inputRefs={loginOtpRefs} />
                      <div style={S.otpFooter}>
                        {loginOtpTimer > 0
                          ? <span style={S.otpTimer}>Resend in <strong style={{ color: '#1B3B5F' }}>{loginOtpTimer}s</strong></span>
                          : <span style={S.otpTimer}></span>}
                        <button style={S.resendBtn(loginOtpTimer > 0)} disabled={loginOtpTimer > 0} onClick={sendLoginOTP}>Resend OTP</button>
                      </div>
                    </div>
                  )}

                  <button className="btn-primary" onClick={handleLogin} disabled={loginLoading}>
                    {loginLoading ? 'Signing In…' : 'Sign In'}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: 18 }}>
                    <button style={{ ...S.backLink, marginBottom: 0 }} onClick={resetLogin}>← Use a different number</button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          SCREEN 2 — GUEST REGISTRATION
      ══════════════════════════════════════ */}


      {/* ══════════════════════════════════════
          SCREEN 3 — FORGOT PASSWORD
      ══════════════════════════════════════ */}
      {screen === 'forgot' && (
        <div className="login-container">
          <LeftPanel
            subtitle="No worries — we'll verify your identity via OTP and let you set a new password instantly."
            features={[
              'OTP sent to your registered mobile',
              'Set a strong new password immediately',
              'No email required — mobile-first recovery',
            ]}
          />

          <div className="login-right">
            <div className="login-card">
              <button style={S.backLink} onClick={() => setScreen('login')}>← Back to Login</button>

              <div className="login-header">
                <h2>Reset Password</h2>
                <p>We'll verify your mobile and reset via OTP</p>
              </div>

              {forgotError   && <div style={S.errBox}>⚠️ {forgotError}</div>}
              {forgotSuccess && (
                <div style={S.succBox}>
                  <div style={S.succIcon}>✓</div>
                  <div style={S.succText}>{forgotSuccess}</div>
                </div>
              )}

              {/* Step 1 — Mobile */}
              {forgotStep === 'mobile' && (
                <div>
                  <div className="form-group">
                    <label className="form-label">Registered Mobile Number</label>
                    <div style={S.inputWrap}>
                      <span style={S.inputPrefix}>+91</span>
                      <input type="tel" className="form-input has-prefix"
                        placeholder="98765 43210" maxLength={10}
                        value={forgotMobile}
                        onChange={e => setForgotMobile(e.target.value.replace(/\D/g,''))}
                        onKeyDown={e => e.key === 'Enter' && sendForgotOTP()}
                      />
                    </div>
                  </div>
                  <button className="btn-primary" onClick={sendForgotOTP} disabled={forgotLoading}>
                    {forgotLoading ? 'Sending…' : 'Send OTP'}
                  </button>
                </div>
              )}

              {/* Step 2 — OTP */}
              {forgotStep === 'otp' && (
                <div>
                  <div className="form-group">
                    <label className="form-label">OTP sent to +91 {forgotMobile.slice(0,5)} {forgotMobile.slice(5)}</label>
                    <OtpRow values={forgotOtp} setValues={setForgotOtp} inputRefs={forgotOtpRefs} />
                    <div style={S.otpFooter}>
                      {forgotTimer > 0
                        ? <span style={S.otpTimer}>Resend in <strong style={{ color: '#1B3B5F' }}>{forgotTimer}s</strong></span>
                        : <span style={S.otpTimer}></span>}
                      <button style={S.resendBtn(forgotTimer > 0)} disabled={forgotTimer > 0} onClick={sendForgotOTP}>Resend</button>
                    </div>
                  </div>
                  <button className="btn-primary" onClick={verifyForgotOTP} disabled={forgotLoading}>
                    {forgotLoading ? 'Verifying…' : 'Verify OTP'}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <button style={{ ...S.backLink, marginBottom: 0 }} onClick={() => { setForgotStep('mobile'); setForgotError(''); }}>← Change number</button>
                  </div>
                </div>
              )}

              {/* Step 3 — New password */}
              {forgotStep === 'newpw' && (
                <div>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input type="password" className="form-input" placeholder="Enter new password"
                      value={newPw1} onChange={e => setNewPw1(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <input type="password" className="form-input" placeholder="Repeat new password"
                      value={newPw2} onChange={e => setNewPw2(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && saveNewPassword()} />
                  </div>
                  <button className="btn-primary" onClick={saveNewPassword} disabled={forgotLoading}>
                    {forgotLoading ? 'Saving…' : 'Save New Password'}
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoginScreen;